import React, { useState } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity } from 'react-native';
import { User, Mail, Lock, Trash2 } from 'lucide-react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import {
  useTheme,
  SPACING,
  BORDER_RADIUS,
  FONT_SIZES,
  TYPOGRAPHY,
  CONTENT_MAX_WIDTH,
} from '../../src/theme';
import { Input, Button, Avatar, ListItem } from '@components/common';
import Head from 'expo-router/head';
import { Header } from '@components/layout';
import { useAuthStore } from '../../src/store/authStore';
import { useImagePicker } from '@hooks/useImagePicker';
import { appAlert } from '../../src/utils/appAlert';

export default function AccountScreen() {
  const { colors } = useTheme();
  const insets = useSafeAreaInsets();
  const { user, updateProfile, uploadAvatar, sendPasswordResetEmail, deleteAccount, isLoading } =
    useAuthStore();

  const [firstName, setFirstName] = useState(user?.firstName || '');
  const [lastName, setLastName] = useState(user?.lastName || '');
  const [isUploading, setIsUploading] = useState(false);
  const { showPicker } = useImagePicker({ quality: 0.7 });

  const handleChangePhoto = () => {
    if (!user) return;
    showPicker((uri) => handleUploadAvatar(uri));
  };

  const handleUploadAvatar = async (uri: string) => {
    if (!user) return;
    setIsUploading(true);
    try {
      await uploadAvatar(uri);
    } catch {
      appAlert('Error', 'Failed to upload photo. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleSave = async () => {
    if (!firstName.trim() || !lastName.trim()) {
      appAlert('Error', 'Please fill in all fields');
      return;
    }

    try {
      await updateProfile({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
      });
      appAlert('Success', 'Your profile has been updated');
    } catch {
      appAlert('Error', 'Failed to update profile');
    }
  };

  const handleChangePassword = async () => {
    if (!user?.email) return;
    try {
      await sendPasswordResetEmail(user.email);
      appAlert('Check Your Email', `A password reset link has been sent to ${user.email}`);
    } catch {
      appAlert('Error', 'Could not send reset email. Please try again.');
    }
  };

  // Stays a modal (panel-system.md rule 7: irreversible, so a confirm, not
  // an undo toast), and keeps its second "are you sure" step on every
  // platform. It was an Alert.alert chain, which RN-web ignores, so on web
  // it never showed at all; appAlert shows both steps there too. The first
  // step names the account so it's clear which one is about to go.
  const handleDeleteAccount = () => {
    appAlert(
      'Delete Account',
      `This permanently deletes ${user?.email ?? 'your account'} and all your data. This action cannot be undone.`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete My Account',
          style: 'destructive',
          onPress: () => {
            appAlert('Are you sure?', 'Last chance. This is permanent.', [
              { text: 'Cancel', style: 'cancel' },
              {
                text: 'Yes, Delete',
                style: 'destructive',
                onPress: () => {
                  void (async () => {
                    try {
                      await deleteAccount();
                    } catch {
                      appAlert('Error', 'Failed to delete account. Please try again.');
                    }
                  })();
                },
              },
            ]);
          },
        },
      ]
    );
  };

  const hasChanges = firstName.trim() !== user?.firstName || lastName.trim() !== user?.lastName;

  return (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      <Head>
        <title>Account - Redi</title>
      </Head>
      <Header
        showBack
        showAvatar={false}
        rightLabel={hasChanges ? 'Save' : undefined}
        onRightPress={hasChanges ? handleSave : undefined}
      />
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled">
        <View style={[styles.content, { paddingBottom: insets.bottom + SPACING.xl }]}>
          <Text style={[styles.screenTitle, { color: colors.text }]}>Account</Text>

          {/* Avatar */}
          <View style={styles.avatarSection}>
            <Avatar
              imageUrl={user?.avatarUrl}
              name={`${user?.firstName} ${user?.lastName}`}
              size={80}
            />
            <Button
              title={isUploading ? 'Uploading...' : 'Change Photo'}
              onPress={handleChangePhoto}
              variant="ghost"
              size="sm"
              disabled={isUploading}
              loading={isUploading}
              style={styles.changePhotoButton}
            />
          </View>

          {/* Form */}
          <View style={styles.form}>
            <Input
              label="First name"
              value={firstName}
              onChangeText={setFirstName}
              placeholder="Enter first name"
              leftIcon={User}
            />

            <Input
              label="Last name"
              value={lastName}
              onChangeText={setLastName}
              placeholder="Enter last name"
            />

            <Input
              label="Email"
              value={user?.email || ''}
              placeholder="name@example.com"
              leftIcon={Mail}
              editable={false}
              hint="Email cannot be changed"
            />
          </View>

          {/* Save button (mobile) */}
          {hasChanges && (
            <Button
              title="Save Changes"
              onPress={handleSave}
              variant="primary"
              size="lg"
              fullWidth
              loading={isLoading}
              style={styles.saveButton}
            />
          )}

          {/* Security */}
          <Text style={[styles.groupTitle, { color: colors.textSecondary }]}>Security</Text>
          <View style={[styles.groupContent, { borderTopColor: colors.border }]}>
            <ListItem
              icon={Lock}
              label="Change Password"
              onPress={handleChangePassword}
              showDivider
              style={styles.groupRow}
            />
          </View>

          {/* Delete Account */}
          <TouchableOpacity
            style={[styles.deleteButton, { borderColor: colors.dangerBorder }]}
            onPress={handleDeleteAccount}
            disabled={isLoading}
            activeOpacity={0.7}>
            <Trash2 size={18} color={colors.danger} />
            <Text style={[styles.deleteText, { color: colors.danger }]}>Delete Account</Text>
          </TouchableOpacity>
        </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    alignItems: 'center',
  },
  content: {
    width: '100%',
    maxWidth: CONTENT_MAX_WIDTH,
    paddingHorizontal: SPACING.md,
    paddingTop: SPACING.lg,
  },
  screenTitle: {
    ...TYPOGRAPHY.screenTitle,
    marginBottom: SPACING.lg,
  },
  avatarSection: {
    alignItems: 'center',
    marginBottom: SPACING.xl,
  },
  changePhotoButton: {
    marginTop: SPACING.sm,
  },
  form: {
    marginBottom: SPACING.lg,
  },
  saveButton: {
    marginTop: SPACING.sm,
    marginBottom: SPACING.lg,
  },
  groupTitle: {
    ...TYPOGRAPHY.overline,
    marginTop: SPACING.lg,
    marginBottom: SPACING.sm,
  },
  // Hairline row, not a filled panel (mockup `.rowlink`).
  groupContent: {
    borderTopWidth: StyleSheet.hairlineWidth,
    marginBottom: SPACING.lg,
  },
  groupRow: {
    paddingHorizontal: 0,
  },
  // Bordered danger pill, not a pink block.
  deleteButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: SPACING.md,
    borderRadius: BORDER_RADIUS.full,
    borderWidth: StyleSheet.hairlineWidth,
    marginTop: SPACING.sm,
  },
  deleteText: {
    ...TYPOGRAPHY.h4,
    fontSize: FONT_SIZES.md,
    marginLeft: SPACING.sm,
  },
});
